import type { Guide } from "../types";

// ── Account & Settings ───────────────────────────────────────────────────────
// Everything under Account Settings (reached from the sidebar user menu →
// Account Settings, which opens the /account area): your personal profile, the
// people in your account, roles and groups, your plan and screen licenses,
// screen settings, Single sign-on, proof of play, the Developer tab, the player
// downloads, and audit logs.

export const accountGuides: Guide[] = [
  {
    slug: "profile-and-preferences",
    category: "account",
    route: "/account/profile-and-preferences",
    title: "Your profile & preferences",
    summary:
      "Update your name and password on the My Account tab, and pick your dashboard language.",
    intro:
      "Your profile controls how you appear to teammates. Everything here is personal — changes affect only your own login, not your colleagues.",
    prerequisites: ["You are signed in to the dashboard — any role can change their own profile and password."],
    readingTime: 2,
    status: "draft",
    lastUpdated: "2026-10-01",
    steps: [
      {
        title: "Open your account",
        blocks: [
          {
            type: "p",
            text: "Click your name at the bottom of the sidebar to open the user menu. The Owner, Admins and Workspace admins choose Account Settings (gear icon); everyone else chooses My account (person icon). Both open the Account area on the My Account tab.",
          },
          {
            type: "note",
            tone: "info",
            text: "The other tabs you see there depend on your role. Editors, Content managers and Viewers see My Account and Downloads, plus Proof of play when they can see screens.",
          },
        ],
        screenshot: {
          src: "account/profile-and-preferences/01.png",
          alt: "The sidebar user menu of the Owner open, with Account Settings and Logout",
          caption: "Your account is in the sidebar user menu — Account Settings for admins, My account for everyone else.",
          todo: "Sidebar user menu open (click the name at the bottom of the sidebar), 'Account Settings' item highlighted. Use the Acme Retail demo account.",
        },
      },
      {
        title: "Edit your profile",
        blocks: [
          {
            type: "p",
            text: "In the Profile card, update your Full name, First name, Last name, Avatar URL, Job title and Job function, then click Save profile. Your email is shown but can't be changed here.",
          },
        ],
        screenshot: {
          src: "account/profile-and-preferences/02.png",
          alt: "The My Account tab: the Profile card on the left and the Change password card on the right",
          caption: "The My Account tab holds your personal details.",
          todo: "My Account tab of the Acme Retail demo account, current tab row (Users, Roles, Groups, Invitations…).",
        },
      },
      {
        title: "Change your password",
        blocks: [
          {
            type: "p",
            text: "In the Change password card, enter your Current password, then your New password twice (New password and Confirm new password), and click Change password. A password must be at least 8 characters.",
          },
          {
            type: "note",
            tone: "tip",
            text: "If your organization signs in with single sign-on, you sign in through your identity provider instead — see Single sign-on.",
          },
        ],
        screenshot: {
          src: "account/profile-and-preferences/03.png",
          alt: "The Change password card with Current password, New password and Confirm new password",
          todo: "Close-up of the Change password card.",
        },
      },
      {
        title: "Choose your language",
        blocks: [
          {
            type: "p",
            text: "Use the language switcher near the bottom of the sidebar (it shows the current language, for example English) to pick English, العربية or Français. The dashboard switches straight away; Arabic also turns the layout right-to-left.",
          },
          {
            type: "p",
            text: "The choice is remembered in the browser you're using, so each teammate can pick their own.",
          },
        ],
        screenshot: {
          src: "account/profile-and-preferences/04.png",
          alt: "The sidebar language switcher open, listing English, العربية and Français",
          caption: "Pick your language from the sidebar.",
          todo: "Sidebar language switcher open with the three languages listed.",
        },
      },
    ],
    related: [
      { label: "Users & invitations", to: "/account/users-and-invitations" },
      { label: "Single sign-on (SSO)", to: "/account/single-sign-on" },
    ],
    keywords: ["account", "profile", "password", "language", "arabic", "french", "my account"],
  },

  {
    slug: "two-factor-authentication",
    category: "account",
    route: "/account/two-factor-authentication",
    title: "Two-factor authentication",
    summary: "Add a code from an authenticator app to your password, and require it for your whole team.",
    intro:
      "Two-factor authentication (2FA) asks for a 6-digit code from an authenticator app on your phone after your password. Someone who learns your password still can't sign in without your phone.",
    prerequisites: [
      "An authenticator app on your phone: Google Authenticator, Microsoft Authenticator, 1Password or Authy.",
      "To require it for everyone: you are the Owner or an Admin.",
    ],
    readingTime: 3,
    status: "draft",
    lastUpdated: "2026-10-03",
    steps: [
      {
        title: "Turn on two-factor for your account",
        blocks: [
          {
            type: "ol",
            items: [
              "Open My account. Find the Two-factor authentication card and click Enable two-factor.",
              "Scan the QR code with your authenticator app. If you can't scan it, type the Manual setup key into the app instead.",
              "Type the 6-digit code the app shows and click Confirm.",
              "Save your recovery codes somewhere safe (a password manager is ideal). Each code works once.",
            ],
          },
          {
            type: "note",
            tone: "warning",
            text: "Your recovery codes are the only way in if you lose your phone. If you've used most of them, click Regenerate recovery codes for a fresh set.",
          },
        ],
      },
      {
        title: "Sign in with two-factor",
        blocks: [
          {
            type: "p",
            text: "After your email and password, PulsePanels asks for the 6-digit code from your authenticator app. If you don't have your phone, type one of your recovery codes instead.",
          },
        ],
      },
      {
        title: "Require two-factor for everyone in your organization",
        blocks: [
          {
            type: "p",
            text: "Owners and Admins see the Organization two-factor policy card in My account:",
          },
          {
            type: "ul",
            items: [
              "Optional (the default) — members may turn on two-factor themselves.",
              "Required — every member must set it up. Anyone who hasn't yet is asked to set it up the next time they sign in, before they can use PulsePanels.",
              "Trust SSO for two-factor — when someone signs in with Google, Microsoft or your single sign-on provider, the provider's own sign-in is trusted and PulsePanels doesn't ask for a code.",
            ],
          },
          {
            type: "note",
            tone: "tip",
            text: "Turn on two-factor for your own account first, then switch the policy to Required, so you can check the sign-in yourself.",
          },
        ],
      },
      {
        title: "When someone loses their phone",
        blocks: [
          {
            type: "p",
            text: "They can sign in with one of their recovery codes. If they have none left, an Owner or Admin opens Account Settings → Users, opens the menu next to that person and chooses Reset two-factor. They set it up again the next time they sign in.",
          },
        ],
      },
      {
        title: "Turn it off",
        blocks: [
          {
            type: "p",
            text: "In My account, click Disable two-factor and enter a current code (or a recovery code) to confirm. You can't turn it off while your organization requires two-factor.",
          },
        ],
      },
    ],
    related: [
      { label: "My account", to: "/account/profile-and-preferences" },
      { label: "Users & invitations", to: "/account/users-and-invitations" },
      { label: "Single sign-on (SSO)", to: "/account/single-sign-on" },
    ],
    keywords: ["two-factor", "2fa", "mfa", "authenticator", "totp", "security", "recovery codes", "sign in", "login", "verification code"],
  },

  {
    slug: "who-sees-what",
    category: "account",
    route: "/account/who-sees-what",
    title: "Who sees what: people & Spaces",
    summary:
      "The two rules that decide what each person sees when they sign in — explained with one example team.",
    intro:
      "Your account can be split into Spaces — for example one per branch, building or department. Every screen, playlist, channel, media file and app belongs to one Space. Who sees which Space comes down to two simple rules.",
    readingTime: 2,
    status: "published",
    lastUpdated: "2026-10-01",
    steps: [
      {
        title: "Rule 1 — the Owner and Admins see every Space",
        blocks: [
          {
            type: "p",
            text: "The Owner and anyone with the Admin role — given to them directly or through a group — see all Spaces, automatically, including Spaces created later. You never add them to a Space.",
          },
        ],
        screenshot: {
          src: "account/who-sees-what/01.png",
          alt: "The Space selector of the account Owner, open, listing All spaces, Head office and Mall shop",
          caption: "The Owner's Space selector: every Space, plus “All spaces”.",
        },
      },
      {
        title: "Rule 2 — everyone else sees only the Spaces you give them",
        blocks: [
          {
            type: "p",
            text: "Everyone else sees only the Spaces they were given — one by one, or through a group — and nothing else. In each of those Spaces you also choose what they can do (their role there).",
          },
        ],
        screenshot: {
          src: "account/who-sees-what/02.png",
          alt: "The Space selector of Lina, who was given only Mall shop, open, listing only Mall shop",
          caption: "A team member given only one Space sees only that Space.",
        },
      },
      {
        title: "An example team",
        blocks: [
          { type: "p", text: "Acme Retail has two Spaces, “Head office” and “Mall shop”, and three people:" },
          {
            type: "table",
            head: ["Person", "Role", "Sees"],
            rows: [
              ["Sara", "Owner", "Head office and Mall shop — everything"],
              ["Omar", "Admin", "Head office and Mall shop — everything except changing the Owner"],
              ["Lina", "Content manager in Mall shop", "Only Mall shop — its screens and its content"],
            ],
          },
          {
            type: "note",
            tone: "tip",
            text: "People who see more than one Space switch between them with the Space selector at the top of the sidebar. The screens and content on every page follow the selected Space.",
          },
        ],
      },
    ],
    related: [
      { label: "Set up your team", to: "/getting-started/invite-your-team" },
      { label: "Examples for your organization", to: "/account/team-examples" },
      { label: "Roles & permissions", to: "/account/roles-and-permissions" },
      { label: "Groups", to: "/account/groups" },
    ],
    keywords: ["spaces", "workspace", "access", "who can see", "users", "permissions", "owner", "admin", "groups"],
  },

  {
    slug: "team-examples",
    category: "account",
    route: "/account/team-examples",
    title: "Examples: Spaces and roles for your organization",
    summary:
      "Ready-made setups for a university, a hospital, shops in several malls or countries, a hotel group and an office building.",
    intro:
      "Create one Space for each group of screens that is looked after by the same people. Then give each person only the Spaces they look after, with the role they need there. There is no separate “region” level: someone who covers several Spaces is simply given each of them.",
    readingTime: 4,
    status: "published",
    lastUpdated: "2026-09-29",
    steps: [
      {
        title: "University",
        blocks: [
          { type: "p", text: "Spaces: Main campus · Engineering faculty · Medical faculty · Student housing" },
          {
            type: "table",
            head: ["Person", "Role", "Spaces", "Why"],
            rows: [
              ["Director of communications", "Admin", "All", "Publishes university-wide announcements"],
              ["Engineering web officer", "Content manager", "Engineering faculty", "Runs the faculty's lobby and corridor screens"],
              ["Medical faculty secretary", "Editor", "Medical faculty", "Updates timetables and notices — doesn't touch the screens"],
              ["Housing office", "Content manager", "Student housing", "Dormitory screens"],
              ["Dean's office", "Viewer", "Main campus", "Wants to see what is on air"],
            ],
          },
        ],
      },
      {
        title: "Hospital",
        blocks: [
          { type: "p", text: "Spaces: Public areas · Outpatient clinics · Emergency department · Staff areas" },
          {
            type: "table",
            head: ["Person", "Role", "Spaces", "Why"],
            rows: [
              ["Head of patient experience", "Admin", "All", "Owns all patient-facing content"],
              ["Safety manager", "Admin", "All", "Sends emergency alerts — only the Owner and Admins can"],
              ["Clinics coordinator", "Content manager", "Outpatient clinics", "Waiting-room and queue screens"],
              ["Emergency department nurse", "Editor", "Emergency department", "Waiting-time and information slides"],
              ["HR officer", "Editor", "Staff areas", "Staff notices — never sees patient areas"],
            ],
          },
          {
            type: "note",
            tone: "tip",
            text: "Keeping staff screens in their own Space means staff-only content can never end up on a public screen by mistake.",
          },
        ],
      },
      {
        title: "Shops in several malls and countries",
        blocks: [
          {
            type: "p",
            text: "One Space per shop, for example Doha – North Mall, Doha – City Mall, Dubai – Marina Mall and Abu Dhabi – Central Mall. Set each Space's time zone when you create it, so schedules and clocks follow local time.",
          },
          {
            type: "table",
            head: ["Person", "Role", "Spaces", "Why"],
            rows: [
              ["Head of marketing", "Admin", "All", "Company-wide campaigns"],
              ["Qatar regional manager", "Content manager", "The Qatar shops", "Given each Qatar Space — sees Qatar only"],
              ["UAE regional manager", "Content manager", "The UAE shops", "Sees the UAE only"],
              ["North Mall supervisor", "Editor", "Doha – North Mall", "Updates prices and offers for their shop"],
              ["Finance", "Viewer", "All shops", "Checks what is playing; changes nothing"],
            ],
          },
        ],
      },
      {
        title: "Hotel group",
        blocks: [
          { type: "p", text: "One Space per hotel, for example Hotel A – Doha and Hotel B – Muscat." },
          {
            type: "table",
            head: ["Person", "Role", "Spaces", "Why"],
            rows: [
              ["Group brand manager", "Admin", "All", "Brand-wide promotions"],
              ["Hotel A general manager", "Workspace admin", "Hotel A – Doha", "Runs the hotel's screens and invites the hotel's own staff"],
              ["Hotel A front office", "Editor", "Hotel A – Doha", "Welcome messages and event boards"],
              ["Hotel B general manager", "Workspace admin", "Hotel B – Muscat", "The same, for Hotel B"],
            ],
          },
          {
            type: "note",
            tone: "tip",
            text: "A Workspace admin manages the people of their own hotel, so head office doesn't have to handle every staff change.",
          },
        ],
      },
      {
        title: "Office building with several departments",
        blocks: [
          { type: "p", text: "Spaces: Reception & lobby · Sales floor · Factory floor · Meeting rooms" },
          {
            type: "table",
            head: ["Person", "Role", "Spaces", "Why"],
            rows: [
              ["IT manager", "Admin", "All", "Installs and pairs every screen"],
              ["Marketing officer", "Content manager", "Reception & lobby, Sales floor", "Brand and sales content"],
              ["Production supervisor", "Editor", "Factory floor", "Safety notices and production figures"],
              ["Office administrator", "Editor", "Meeting rooms", "Room booking displays"],
            ],
          },
        ],
      },
      {
        title: "The same four questions for any organization",
        blocks: [
          {
            type: "ol",
            items: [
              "Where are the screens? — one Space per place or team.",
              "Who should see everything? — Admin. Keep this to very few people.",
              "Who looks after only some places? — give them just those Spaces.",
              "What can they do there? — look only: Viewer · edit content: Editor · also pair screens and choose what they play: Content manager · also manage that Space's people: Workspace admin. Need something in between? Create your own role.",
            ],
          },
        ],
      },
    ],
    related: [
      { label: "Who sees what: people & Spaces", to: "/account/who-sees-what" },
      { label: "Set up your team", to: "/getting-started/invite-your-team" },
      { label: "Roles & permissions", to: "/account/roles-and-permissions" },
    ],
    keywords: ["examples", "university", "hospital", "retail", "malls", "hotel", "regions", "spaces", "roles"],
  },

  {
    slug: "users-and-invitations",
    category: "account",
    route: "/account/users-and-invitations",
    title: "Users & invitations",
    summary: "Invite someone into the right Space, see who has access, and change or remove access later.",
    intro:
      "Owners and Admins manage everyone from Account Settings → Users. Inviting takes one dialog: the person's email, their role and — unless they are an Admin — the Space they get.",
    prerequisites: ["You are the Owner or an Admin. (A Workspace admin manages the people of their own Space — see “For Workspace admins”.)"],
    readingTime: 3,
    status: "published",
    lastUpdated: "2026-10-01",
    steps: [
      {
        title: "See who has access",
        blocks: [
          {
            type: "p",
            text: "Account Settings → Users lists everyone. The Role column shows each person's role, with the Spaces they see written underneath (“All Spaces” for the Owner and Admins). A role someone holds through a group is written under it too — for example “Admin through the “Operations” group”.",
          },
        ],
        screenshot: {
          src: "account/users-and-invitations/01.png",
          alt: "The Users tab: each person with their role and, underneath, the Spaces they see",
          caption: "Role and Spaces for every person.",
        },
      },
      {
        title: "Invite someone",
        blocks: [
          { type: "p", text: "Click Invite User and enter their email. Then choose a Role:" },
          {
            type: "ul",
            items: [
              "Admin — sees every Space. No Space to choose.",
              "Workspace admin, Content manager, Editor or Viewer — choose the Space they get. They will see only that Space.",
            ],
          },
          { type: "p", text: "Click Create invitation. They receive an email and appear in the list as Invited." },
        ],
        screenshot: {
          src: "account/users-and-invitations/02.png",
          alt: "The Invite user dialog: email, Role set to Content manager, Space set to Mall shop",
          caption: "Every role except Admin asks which Space.",
        },
      },
      {
        title: "Give more Spaces, or change a role in one Space",
        blocks: [
          { type: "p", text: "On the person's row, click the key icon (Manage access). The list under Workspace access is exactly what they see." },
          {
            type: "ol",
            items: [
              "To give another Space: pick it in “Add to space…”, pick a role, and click Add.",
              "To take a Space away: click Remove next to it.",
              "To change what they can do in one Space: change the role on that Space's row.",
            ],
          },
          {
            type: "note",
            tone: "info",
            text: "This works even before they have accepted the invitation — the invitation follows them to the Spaces they keep.",
          },
          {
            type: "p",
            text: "Roles that come from a group are listed under “Access via groups”. Change those in Account Settings → Groups.",
          },
          {
            type: "note",
            tone: "info",
            text: "Your own row and the Owner's row are read-only. Manage access still shows them, with a note — “You can't change your own access. Ask another admin to do it.” or “The Owner always has access to every Space, so it can't be changed here.”",
          },
        ],
        screenshot: {
          src: "account/users-and-invitations/03.png",
          alt: "The Manage access dialog for Maya: a Mall shop row with her role and a Remove button, the Add to space row, and her access via the Store managers group",
          caption: "Manage access: one row per Space.",
        },
      },
      {
        title: "Follow pending invitations",
        blocks: [
          { type: "p", text: "The Invitations tab lists every invitation and whether it is Pending, Accepted or Revoked. Resend one that was missed, or revoke one you no longer want. On the Users tab, someone marked Invited also has a Resend invitation button (the circular arrow)." },
        ],
        screenshot: {
          src: "account/users-and-invitations/04.png",
          alt: "The Invitations tab: accepted invitations and one pending invitation",
          caption: "Pending, accepted and revoked invitations.",
        },
      },
      {
        title: "Pause or remove someone",
        blocks: [
          {
            type: "ul",
            items: [
              "Suspend (the stop icon) blocks their access — including everything their groups give them, and signing in through your single sign-on — until you reactivate them. Reactivating someone who never accepted their invitation puts them back to Invited: they still have to accept it themselves.",
              "Remove (the bin icon) takes them out of your account and out of all its groups. If you invite them again later, they start with no groups.",
              "To take away just one Space, use Manage access → Remove instead.",
            ],
          },
        ],
      },
    ],
    related: [
      { label: "Set up your team", to: "/getting-started/invite-your-team" },
      { label: "Roles & permissions", to: "/account/roles-and-permissions" },
      { label: "For Workspace admins", to: "/account/space-people" },
      { label: "Groups", to: "/account/groups" },
    ],
    keywords: ["users", "invite", "invitation", "members", "access", "spaces", "remove", "suspend", "manage access"],
  },

  {
    slug: "roles-and-permissions",
    category: "account",
    route: "/account/roles-and-permissions",
    title: "Roles & permissions",
    summary: "What each role can do, how to change someone's role, and how to create your own role.",
    intro:
      "The Owner and Admins act in every Space. Everyone else has a role in each Space they were given — and it can differ from Space to Space. If no built-in role fits, you can create your own.",
    readingTime: 4,
    status: "published",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "What each role can do",
        blocks: [
          {
            type: "table",
            head: ["Task", "Owner / Admin", "Workspace admin", "Content manager", "Editor", "Viewer"],
            rows: [
              ["See screens and content", "✓", "✓", "✓", "✓", "✓"],
              ["Create and edit content (media, playlists, channels, apps)", "✓", "✓", "✓", "✓", "—"],
              ["Delete content", "✓", "✓", "✓", "—", "—"],
              ["Pair screens, choose what they play, rename them", "✓", "✓", "✓", "—", "—"],
              ["Unpair, delete or move screens", "✓", "✓", "—", "—", "—"],
              ["Invite and manage people", "✓ everyone", "✓ own Space", "—", "—", "—"],
              ["Create Spaces and roles; emergency alerts; audit logs; single sign-on; screen settings; API keys and webhooks", "✓", "—", "—", "—", "—"],
            ],
          },
          {
            type: "p",
            text: "For everyone but the Owner and Admins, each ✓ applies inside the Spaces they were given. Owner and Admin can do the same things, except that nobody can change or remove the Owner; ownership is transferred by your PulsePanels contact.",
          },
        ],
      },
      {
        title: "Change someone's role",
        blocks: [
          {
            type: "ul",
            items: [
              "Same role in all their Spaces: Account Settings → Users → the Role list on their row. The Spaces they see don't change.",
              "A different role in one Space: the key icon (Manage access) → change the role on that Space's row.",
              "Make someone an Admin: choose Admin in their Role list. Choose any other role to take it away again — they keep only the Spaces listed under their name.",
              "Give a role to several people at once: put them in a group (Account Settings → Groups) and give the group the role.",
            ],
          },
          { type: "note", tone: "tip", text: "Start with the lowest role that lets someone do their job — it's quick to raise later. Changes apply the next time they open or refresh a page." },
        ],
      },
      {
        title: "See what a role allows",
        blocks: [
          {
            type: "p",
            text: "Account Settings → Roles (Owner and Admins) lists every role. Select one to see, in plain language, what it allows. The built-in roles can't be changed.",
          },
        ],
        screenshot: {
          src: "account/roles-and-permissions/01.png",
          alt: "The Roles tab with the built-in Editor role selected and what it allows",
          caption: "The built-in Editor role and what it allows.",
        },
      },
      {
        title: "Create your own role",
        blocks: [
          {
            type: "ol",
            items: [
              "In Account Settings → Roles, click Create custom role — or select a built-in role and click Duplicate to start from it.",
              "Give it a name and tick what it allows, for example See screens + Choose what screens play for someone who only changes what screens show.",
              "Click Create role. It now appears in the role lists of Manage access and People in this Space.",
            ],
          },
          {
            type: "note",
            tone: "info",
            text: "Your own roles are Space roles: they apply in each Space you give them to someone in. They can't grant account-wide power — managing people, Spaces or settings stays with the Owner and Admins.",
          },
        ],
        screenshot: {
          src: "account/roles-and-permissions/03.png",
          alt: "The Create a role dialog: name Screen operator, with See screens and Choose what screens play ticked",
          caption: "Tick what the role allows — each line says what it means.",
        },
      },
      {
        title: "Change or delete your role",
        blocks: [
          {
            type: "p",
            text: "Select your role, change its name, description or what it allows, and click Save Changes. Delete works once nobody has the role any more — give those people another role first.",
          },
        ],
        screenshot: {
          src: "account/roles-and-permissions/02.png",
          alt: "A custom role, Playlist editor, selected on the Roles tab with Duplicate, Delete and Save Changes",
          caption: "Your own role can be edited and deleted; built-in roles can't.",
        },
      },
    ],
    related: [
      { label: "Users & invitations", to: "/account/users-and-invitations" },
      { label: "For Workspace admins", to: "/account/space-people" },
      { label: "Examples for your organization", to: "/account/team-examples" },
      { label: "Audit logs", to: "/account/audit-logs" },
    ],
    keywords: ["roles", "permissions", "custom role", "create role", "editor", "viewer", "admin", "content manager", "workspace admin"],
  },

  {
    slug: "space-people",
    category: "account",
    route: "/account/space-people",
    title: "For Workspace admins: manage your Space's people",
    summary: "As a Workspace admin, invite people into your Space, change their role there, and remove them.",
    intro:
      "A Workspace admin runs one Space — for example a branch or a hotel — including its team. Everything happens in Account Settings → People in this Space.",
    prerequisites: ["You are a Workspace admin of the Space you are working in."],
    readingTime: 2,
    status: "published",
    lastUpdated: "2026-10-01",
    steps: [
      {
        title: "Watch: Maya manages the mall shop's people",
        blocks: [
          {
            type: "video",
            src: "/videos/manage-your-space-people.webm",
            poster: "/videos/manage-your-space-people.png",
            caption: "Maya, Workspace admin of the mall shop, invites Karim, changes Lina's role, and shows Suspend and Remove (1 min).",
          },
        ],
      },
      {
        title: "Open People in this Space",
        blocks: [
          {
            type: "p",
            text: "Account Settings → People in this Space lists everyone who was given your Space and their role there. The Owner and Admins see every Space anyway; they are listed here only if they were also added to your Space, and only they can change each other.",
          },
        ],
        screenshot: {
          src: "account/space-people/01.png",
          alt: "People in this Space for the Mall shop: Maya the Workspace admin, an Admin (read-only), and Content managers such as Lina with Suspend and Remove",
          caption: "Your Space's people, with their role here.",
        },
      },
      {
        title: "Invite, change a role, suspend or remove",
        blocks: [
          {
            type: "ul",
            items: [
              "Invite User — the invitation is always for your own Space.",
              "The role list on a person's row changes what they can do in your Space.",
              "Suspend pauses their access (Reactivate brings it back); the bin removes them from your Space.",
              "While someone is suspended in your Space, a group can't bring them back into it. Only the Owner and Admins, who see every Space, aren't affected.",
            ],
          },
        ],
      },
    ],
    related: [
      { label: "Roles & permissions", to: "/account/roles-and-permissions" },
      { label: "Who sees what: people & Spaces", to: "/account/who-sees-what" },
    ],
    keywords: ["workspace admin", "space admin", "people in this space", "invite", "remove", "branch manager"],
  },

  {
    slug: "groups",
    category: "account",
    route: "/account/groups",
    title: "Groups: give roles to several people at once",
    summary: "Put people in a group, give the group a role, and everyone in it gets that role — in every Space or only the ones you pick.",
    intro:
      "A group saves you from giving the same role to people one by one. Give a role to the group, and everyone in it gets that role; add someone to the group later and they get it too. Groups are managed by the Owner and Admins.",
    prerequisites: ["You are the Owner or an Admin."],
    readingTime: 3,
    status: "published",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Create a group",
        blocks: [
          {
            type: "p",
            text: "Open Account Settings → Groups and click New Group. Give it a name that says who is in it — for example “Operations” or “Mall shop team” — and click Create Group.",
          },
        ],
      },
      {
        title: "Add people",
        blocks: [
          {
            type: "p",
            text: "On the Members tab, click Add members, tick the people and click “Add … selected”. Only people who are already in your account can be added — invite them first under Users.",
          },
        ],
      },
      {
        title: "Give the group a role",
        blocks: [
          { type: "p", text: "On the Roles tab, click Add role and pick one:" },
          {
            type: "ul",
            items: [
              "Admin — applies to the whole account, so every Space.",
              "Auditor — read-only access to the account's users, licenses and audit log. It opens no Spaces: they see only the Spaces they were given.",
              "Workspace admin, Content manager, Editor, Viewer or one of your own roles — under Applies to, choose All Spaces or Specific spaces.",
            ],
          },
          {
            type: "p",
            text: "Click Confirm. Everyone in the group now has that role; the change applies the next time they open or refresh a page.",
          },
          {
            type: "note",
            tone: "info",
            text: "Ownership can't be given through a group. There is one Owner, and ownership is transferred by your PulsePanels contact.",
          },
        ],
      },
      {
        title: "How a group adds up with someone's own role",
        blocks: [
          {
            type: "ul",
            items: [
              "A group can only raise what someone can do, never lower it: if they are an Editor in a Space and their group gives Content manager there, they are a Content manager there.",
              "Account Settings → Users shows it under their role, for example “Admin through the “Operations” group”, and Manage access lists it under “Access via groups”.",
              "Someone suspended in your account gets nothing from their groups. Someone suspended in one Space stays out of that Space, whatever their groups give — unless they are an Admin.",
              "Removing someone from your account also takes them out of all its groups.",
            ],
          },
        ],
      },
      {
        title: "Change or remove access",
        blocks: [
          {
            type: "p",
            text: "Remove a person from the group, remove a role from the group, or delete the group — everyone loses what the group gave them. Every change to a group is recorded in the audit log: who made it and who gained or lost what.",
          },
        ],
      },
    ],
    related: [
      { label: "Who sees what: people & Spaces", to: "/account/who-sees-what" },
      { label: "Roles & permissions", to: "/account/roles-and-permissions" },
      { label: "Users & invitations", to: "/account/users-and-invitations" },
    ],
    keywords: ["groups", "group", "team", "roles", "bulk", "several people", "access", "admin", "spaces"],
  },

  {
    slug: "billing-and-licenses",
    category: "account",
    route: "/account/billing-and-licenses",
    title: "Your plan & screen licenses",
    summary:
      "How PulsePanels plans work, how many screens your license covers, and what to do when your trial or license runs out.",
    intro:
      "Your license decides how many real devices you can pair. You don't pay or renew inside the dashboard — plans, renewals and invoices are handled by your PulsePanels provider (the reseller or PulsePanels contact who set up your account). The dashboard shows where you stand and tells you when it's time to get in touch.",
    readingTime: 3,
    status: "draft",
    lastUpdated: "2026-10-01",
    steps: [
      {
        title: "The plans",
        blocks: [
          {
            type: "p",
            text: "Each plan is a set number of screens for your whole account, shared by all your Spaces. Only real devices count — virtual screens are free and never use a licence slot:",
          },
          {
            type: "table",
            head: ["Plan", "Screens"],
            rows: [
              ["Free trial", "1 screen for 7 days"],
              ["Essential", "1 screen"],
              ["Professional", "Up to 5 screens"],
              ["Business", "Unlimited"],
            ],
          },
          {
            type: "note",
            tone: "info",
            text: "To start a plan, move to a bigger one or renew, contact your provider. They set up the license, and it applies to your account straight away.",
          },
        ],
      },
      {
        title: "Watch the banner at the top of the dashboard",
        blocks: [
          {
            type: "p",
            text: "During a trial, a banner at the top of every page shows how long is left, for example “You're on a free trial — 7 days left of 1 screen.” In the last three days it turns into a reminder to contact your provider.",
          },
          {
            type: "p",
            text: "If the trial or license has ended, the banner says so — for example “Your trial has expired.” or “No active license.” — and you can't pair more screens until it is renewed.",
          },
        ],
        screenshot: {
          src: "account/billing-and-licenses/01.png",
          alt: "The trial banner across the top of the dashboard: “You're on a free trial — 7 days left of 1 screen.”",
          caption: "The banner shows your trial or license status.",
          todo: "Any dashboard page of a trial account (e.g. Acme Retail on Welcome) with the purple trial banner at the top.",
        },
      },
      {
        title: "See how many screens you're using",
        blocks: [
          {
            type: "p",
            text: "Open Screens. The Storage & license overview panel shows your storage and, under License, how many screens are used out of your allowance and how many remain. On the Business plan it shows the screens used and Unlimited. The License status figure on the Welcome dashboard and on Screen Groups shows the same.",
          },
          {
            type: "p",
            text: "Every paired real device counts, whichever Space it is in. Virtual screens (browser tabs launched with Launch virtual screen) don't count, so a screen list with only virtual screens reads 0 screens used.",
          },
        ],
        screenshot: {
          src: "account/billing-and-licenses/02.png",
          alt: "The Storage & license overview panel on the Screens page of a Business account: storage used, and under License the screens used and Unlimited",
          caption: "Screens used — on Business, Unlimited.",
          todo: "Screens page (/screens), scrolled to the 'Storage & license overview' panel (Storage bar + License). Demo org on the Business plan: '0 screens used' (its screens are all virtual) and 'Unlimited'.",
        },
      },
      {
        title: "When all your screens are used",
        blocks: [
          {
            type: "p",
            text: "When real devices use every licence slot, Pair screen still opens the New Screen dialog, but it says: “Your screen quota is full, so only a virtual screen's code can be paired. To add a real device, inactivate a screen or contact your provider.” A real device's code is refused. Launch virtual screen keeps working, because virtual screens never use a slot.",
          },
          {
            type: "ul",
            items: [
              "Free a place: inactivate a real device you don't need right now (the screen's more-actions menu → Inactivate).",
              "Or ask your provider for a bigger plan.",
            ],
          },
        ],
        screenshot: {
          src: "account/billing-and-licenses/03.png",
          alt: "The New Screen dialog on a full 1-screen trial, saying only a virtual screen's code can be paired",
          caption: "A full quota still lets you pair a virtual screen.",
          todo: "Trial account (1 screen) whose one slot is used by a real device: Screens → Pair screen, the New Screen dialog with the 'Your screen quota is full, so only a virtual screen's code can be paired…' notice.",
        },
      },
      {
        title: "If your license has expired",
        blocks: [
          {
            type: "p",
            text: "Without a valid licence, Pair screen and Launch virtual screen are greyed out with the hint “No active license. Contact your provider to renew.”, and publishing may be refused with “Your license is suspended — contact your provider to publish.” Contact your provider to renew or extend the license. As soon as they extend it, you can pair and publish again — there is nothing to restart.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Invoices and receipts come from your provider as well. Ask them for copies if your finance team needs them.",
          },
        ],
      },
    ],
    related: [
      { label: "Pair a screen", to: "/screens/pair-a-screen" },
      { label: "The screen “more actions” menu", to: "/screens/more-actions" },
    ],
    keywords: ["billing", "license", "licence", "plan", "trial", "subscription", "quota", "essential", "professional", "business", "renew", "invoice", "expired"],
  },

  {
    slug: "screen-settings",
    category: "account",
    route: "/account/screen-settings",
    title: "Screen settings: kiosk PIN, player updates and offline alerts",
    summary:
      "Set the kiosk PIN, choose whether players update automatically, and get an email when a screen goes offline.",
    intro:
      "The Screen settings tab holds three settings that apply to every screen in your account: the kiosk lock PIN, automatic player updates, and screen-down alerts.",
    prerequisites: ["You are the Owner or an Admin."],
    readingTime: 3,
    status: "draft",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open Screen settings",
        blocks: [
          {
            type: "p",
            text: "Open Account Settings and click the Screen settings tab. You'll see three sections: Kiosk lock, Player updates and Screen-down alerts.",
          },
        ],
        screenshot: {
          src: "account/screen-settings/01.png",
          alt: "The Screen settings tab with the Kiosk lock, Player updates and Screen-down alerts sections",
          caption: "Three settings for all your screens.",
          todo: "Screen settings tab (/account/screen-settings) of the Acme Retail demo account, with the current tab row.",
        },
      },
      {
        title: "Set the kiosk PIN",
        blocks: [
          {
            type: "p",
            text: "The player runs locked on the screen. The kiosk PIN lets someone on site exit the player for a moment — for example to change Wi-Fi or the display resolution. There is one PIN for the whole account; every paired screen accepts it.",
          },
          {
            type: "ol",
            items: [
              "Click Set PIN (or Change PIN if one is already set).",
              "Enter a New PIN of 4 to 8 digits, repeat it under Confirm PIN, and click Save PIN.",
            ],
          },
          { type: "subheading", text: "How operators unlock a screen" },
          {
            type: "ul",
            items: [
              "Touch screens: long-press any corner of the screen for 3 seconds.",
              "Mouse or trackpad: right-click anywhere on the screen.",
              "Remote or keyboard: type 0000 followed by the PIN.",
              "Windows player: press Ctrl+Shift+Q, then enter the PIN.",
            ],
          },
          {
            type: "note",
            tone: "warning",
            text: "Clear PIN removes the lock: anyone standing at a screen could then exit the player. Only do this while the screens are in a secure room.",
          },
        ],
        screenshot: {
          src: "account/screen-settings/02.png",
          alt: "The Set kiosk PIN dialog with New PIN and Confirm PIN",
          todo: "Screen settings → click Set PIN / Change PIN: the 'Set kiosk PIN' (or 'Change kiosk PIN') dialog with New PIN (4–8 digits) and Confirm PIN.",
        },
      },
      {
        title: "Choose how players update",
        blocks: [
          {
            type: "p",
            text: "Under Player updates, the Automatic updates switch decides how your Android players get new versions. The change is saved as soon as you flip it.",
          },
          {
            type: "ul",
            items: [
              "On — screens install a new player version automatically as soon as it is released.",
              "Off — you update screens yourself: open Screens → Wall View, select the screen and click Update now next to its app version (it appears when a newer version is available).",
            ],
          },
        ],
      },
      {
        title: "Get an email when a screen goes offline",
        blocks: [
          {
            type: "p",
            text: "Under Screen-down alerts, turn on Email me when a screen goes offline. Then set:",
          },
          {
            type: "ul",
            items: [
              "Consider a screen offline after — how many minutes without a heartbeat count as offline.",
              "Send to — the email addresses to notify, separated by commas. Leave it blank to notify everyone in your account.",
            ],
          },
          {
            type: "p",
            text: "Click Save. You get one email when a screen stops reporting and another when it comes back.",
          },
        ],
        screenshot: {
          src: "account/screen-settings/03.png",
          alt: "Screen-down alerts turned on, with the offline-after minutes, the Send to field and Save",
          todo: "Screen settings scrolled to 'Screen-down alerts' with the switch ON so 'Consider a screen offline after', 'Send to' (use ops@example.com) and Save are visible.",
        },
      },
    ],
    related: [
      { label: "The screen “more actions” menu", to: "/screens/more-actions" },
      { label: "View the Wall", to: "/screens/view-wall" },
      { label: "Download the player apps", to: "/account/downloads" },
    ],
    keywords: ["screen settings", "kiosk", "pin", "unlock", "auto update", "player update", "offline alert", "email alert", "screen down"],
  },

  {
    slug: "single-sign-on",
    category: "account",
    route: "/account/single-sign-on",
    title: "Single sign-on (SSO)",
    summary:
      "Connect your SAML identity provider so your team signs in with their work account.",
    intro:
      "Single sign-on lets your team sign in through your own identity provider — Okta, Microsoft Entra ID, Google Workspace, OneLogin, Ping or any other SAML 2.0 provider — instead of a separate PulsePanels password. People who sign in for the first time are added to your account automatically.",
    prerequisites: [
      "You are the Owner or an Admin.",
      "You can create an app (a SAML integration) in your identity provider.",
    ],
    readingTime: 4,
    status: "draft",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the Single sign-on tab",
        blocks: [
          {
            type: "p",
            text: "Open Account Settings and click Single sign-on. SSO status shows Disabled until you have connected your identity provider.",
          },
        ],
        screenshot: {
          src: "account/single-sign-on/01.png",
          alt: "The Single sign-on tab: SSO status Disabled and the Identity provider (IdP) card",
          caption: "Single sign-on has its own tab.",
          todo: "Single sign-on tab (/account/sso) of the Acme Retail demo account, current tab row.",
        },
      },
      {
        title: "Create the PulsePanels app in your identity provider",
        blocks: [
          {
            type: "p",
            text: "Scroll to Service provider details. Copy the three values into the new SAML app in your identity provider:",
          },
          {
            type: "ul",
            items: [
              "ACS / Reply URL",
              "Entity ID / Audience",
              "SP metadata URL — if your provider can read metadata, this one is enough.",
            ],
          },
        ],
        screenshot: {
          src: "account/single-sign-on/04.png",
          alt: "Provisioning & access and Service provider details, with a Copy button next to each value",
          caption: "Copy these into your identity provider.",
          todo: "Single sign-on tab scrolled down: 'Provisioning & access' and 'Service provider details' (ACS / Reply URL, Entity ID / Audience, SP metadata URL). Current image is from the local demo, so the URLs read http://helpdemo.localhost:5197/api/sso/<id>/… instead of the product host.",
        },
      },
      {
        title: "Enter your identity provider's details",
        blocks: [
          {
            type: "p",
            text: "Back in PulsePanels, fill in the Identity provider (IdP) card with the values from your provider:",
          },
          {
            type: "ul",
            items: [
              "Sign-on URL — the provider's SAML 2.0 single sign-on address.",
              "Issuer / Entity ID (optional) — your provider's identifier.",
              "Signing certificate (X.509 PEM) — the certificate your provider signs with.",
            ],
          },
        ],
        screenshot: {
          src: "account/single-sign-on/02.png",
          alt: "The Identity provider (IdP) card with Sign-on URL, Issuer / Entity ID and Signing certificate",
          caption: "Paste your provider's details.",
          todo: "Identity provider (IdP) card filled with example values (https://idp.example.com/…).",
        },
      },
      {
        title: "Choose who signs in, and with which role",
        blocks: [
          {
            type: "ul",
            items: [
              "Claimed email domains — for example acme.com. Anyone signing in with an email at these domains is sent to your identity provider.",
              "Default role for new users — Viewer, Editor or Admin. It is given to people the first time they sign in. Change their role or Spaces later in Account Settings → Users.",
              "Enforce SSO — everyone must sign in through your identity provider; password sign-in is turned off for them. The Owner keeps password sign-in, so you can't be locked out.",
            ],
          },
        ],
      },
      {
        title: "Save, test and switch it on",
        blocks: [
          {
            type: "ol",
            items: [
              "Click Save changes.",
              "Click Test connection. “Configuration is valid.” means PulsePanels can use your provider's details.",
              "Tick the SSO status box so it reads Active, and click Save changes again. (The box can only be ticked once the Sign-on URL and certificate are filled in.)",
              "In a private browser window, go to the sign-in page, type a work email at your claimed domain and click Continue with SSO.",
            ],
          },
          {
            type: "note",
            tone: "warning",
            text: "Keep your current session open in another window until a test sign-in works — especially before you tick Enforce SSO.",
          },
        ],
        screenshot: {
          src: "account/single-sign-on/03.png",
          alt: "Save changes and Test connection, with “Configuration is valid.” shown after the test",
          todo: "Bottom of the Single sign-on tab after clicking Test connection: 'Configuration is valid.' next to Save changes / Test connection.",
        },
      },
      {
        title: "When someone can't sign in",
        blocks: [
          {
            type: "p",
            text: "Signing in through your identity provider never brings back someone you suspended. They see “Your access has been suspended. Contact your organization's administrator.” — reactivate them in Account Settings → Users if they should have access again.",
          },
          {
            type: "p",
            text: "A deactivated account sees “This account has been deactivated. Contact your organization's administrator.”",
          },
        ],
      },
    ],
    related: [
      { label: "Users & invitations", to: "/account/users-and-invitations" },
      { label: "Roles & permissions", to: "/account/roles-and-permissions" },
    ],
    keywords: ["sso", "saml", "single sign-on", "okta", "entra", "azure ad", "google workspace", "identity provider", "idp", "login", "enforce"],
  },

  {
    slug: "proof-of-play",
    category: "account",
    route: "/account/proof-of-play",
    title: "Proof of play & Proof of Operation",
    summary:
      "Show what played on your screens and for how long, export it as CSV, and share a one-screen Proof of Operation report as a link or PDF.",
    intro:
      "PulsePanels gives you two kinds of proof. The Proof of play report shows what each screen showed and for how long — ideal for advertisers. A Proof of Operation report shows that one screen was online and playing over a period, as a page or PDF you can send to a client.",
    prerequisites: [
      "Your screens have been online and showing content.",
      "You can see screens — then the Proof of play tab is in your account (user menu → Account Settings or My account). Unless you are the Owner or an Admin, you see only the screens of the Space you are in.",
    ],
    readingTime: 4,
    status: "draft",
    lastUpdated: "2026-10-01",
    steps: [
      {
        title: "Open Proof of play",
        blocks: [
          {
            type: "p",
            text: "Open your account from the sidebar user menu and click Proof of play. The report opens on the last 7 days for all screens.",
          },
          {
            type: "p",
            text: "At the top you see the total hours on air, how many screens reported, and how many content placements there were.",
          },
        ],
        screenshot: {
          src: "account/proof-of-play/01.png",
          alt: "The Proof of play tab: date filters, Screen and Group filters, Download CSV, summary figures and the report table",
          caption: "What each screen showed, and for how long.",
          todo: "Proof of play tab (/account/proof-of-play) of the Acme Retail demo account with generic screen names (e.g. Lobby Screen).",
        },
      },
      {
        title: "Choose the dates and screens",
        blocks: [
          {
            type: "p",
            text: "Set From and To, then pick one screen under Screen or a screen group under Group. The report updates on its own — match it to a campaign's dates.",
          },
        ],
        screenshot: {
          src: "account/proof-of-play/02.png",
          alt: "The From, To, Screen and Group filters above the report",
          todo: "Close-up of the From / To / Screen / Group filters with one screen selected.",
        },
      },
      {
        title: "Read the report",
        blocks: [
          {
            type: "p",
            text: "Each row is one screen and what it played: On air (total time), Loops/hr, Sessions (separate on-air stretches), and the First and Last time it was seen. Click a channel row to open its items, with each item's Type, Slot, On air, Plays and Loops/hr.",
          },
          {
            type: "note",
            tone: "info",
            text: "Per-item figures are worked out from the channel's rotation over the measured airtime. They show scheduled airtime, not a count confirmed by the device for every single play.",
          },
        ],
        screenshot: {
          src: "account/proof-of-play/03.png",
          alt: "A channel row expanded to show each item's Type, Slot, On air, Plays and Loops/hr",
          caption: "Open a channel to see every item.",
          todo: "Proof of play with a channel row expanded (chevron open) showing the Item / Type / Slot / On air / Plays / Loops/hr sub-table.",
        },
      },
      {
        title: "Download the report",
        blocks: [
          {
            type: "p",
            text: "Click Download CSV to save the report for the dates and screens you picked — ready for a spreadsheet or to send to an advertiser.",
          },
        ],
      },
      {
        title: "Share a Proof of Operation report for one screen",
        blocks: [
          {
            type: "ol",
            items: [
              "Open Screens → Wall View and select the screen.",
              "Open the Controls tab and, under Quick actions, click Proof of Operation.",
              "Pick Last 6 hours, Last 24 hours or Last 7 days, and click Generate report.",
              "Check the preview — it is exactly what your client will see: a status such as Online & Playing, how much of the period the screen was confirmed running, and a timeline.",
              "Click Copy shareable link, or Download PDF.",
            ],
          },
          {
            type: "note",
            tone: "tip",
            text: "Anyone with the link can open the report without signing in. The report is honest: if there was a real problem, such as a long gap or repeated restarts, it says so plainly.",
          },
        ],
        screenshot: {
          src: "account/proof-of-play/04.png",
          alt: "The Proof of Operation dialog with a generated report, Copy shareable link and Download PDF",
          todo: "Screens → Wall View → select a screen → Controls → Proof of Operation → Last 6 hours → Generate report; dialog scrolled to the Copy shareable link / Download PDF buttons. Current image (local demo, recaptured 2026-10-01) still says Issue detected: the demo's screens had a 2 h 25 m telemetry gap inside the last 6 h (the demo was restarted). Recapture after 6 h+ of uninterrupted Live so it reads Online & Playing.",
        },
      },
    ],
    related: [
      { label: "View the Wall", to: "/screens/view-wall" },
      { label: "Understand screen health", to: "/screens/screen-health" },
      { label: "Audit logs", to: "/account/audit-logs" },
    ],
    keywords: ["proof of play", "proof of operation", "report", "airtime", "playback", "csv", "pdf", "advertiser", "uptime", "online & playing"],
  },

  {
    slug: "developer",
    category: "account",
    route: "/account/developer",
    title: "Developer: API keys & webhooks",
    summary:
      "Create an API key to read your screens and content from your own systems, and get a webhook when a screen goes offline or comes back.",
    intro:
      "The Developer tab is for your IT team or integrator, for example to connect a building-management system. An API key gives read-only access to your screens and content library, limited to what you choose. A webhook sends a signed message to your own system the moment a screen goes offline or comes back online.",
    prerequisites: ["You are the Owner or an Admin."],
    readingTime: 4,
    status: "draft",
    lastUpdated: "2026-10-02",
    steps: [
      {
        title: "Open the Developer tab",
        blocks: [
          {
            type: "p",
            text: "Open Account Settings and click Developer. Quick start shows the Base URL, how to send your key (as a bearer token or the X-API-Key header), and the endpoints:",
          },
          {
            type: "table",
            head: ["Endpoint", "What it returns"],
            rows: [
              ["GET /v1/ping", "Checks a key and shows its organization, access and limits"],
              ["GET /v1/screens", "Your screens, whether they are online, and what they are playing"],
              ["GET /v1/screens/:id", "One screen, by its id or by your own External ID as ext:YOUR-ID"],
              ["GET /v1/channels, /v1/playlists, /v1/media, /v1/apps", "Your content library, one type at a time"],
              ["GET /v1/channels/:id (and playlists, media, apps)", "One item with its details"],
            ],
          },
          {
            type: "p",
            text: "Click OpenAPI spec to download the full reference. Your integrator can import it into Postman or a code generator, or read the API documentation in this Help Center.",
          },
          { type: "steps-ref", label: "API documentation", to: "/developers/api-overview" },
          {
            type: "note",
            tone: "tip",
            text: "Give a screen your own ID, such as an asset tag or BMS point name: open the screen, and in its settings fill in External ID under Location & ID.",
          },
        ],
        screenshot: {
          src: "account/developer/01.png",
          alt: "The Developer tab: Quick start with the Base URL, authentication example and endpoints",
          todo: "Developer tab (/account/developer) of the Acme Retail demo account showing Quick start and the API keys list.",
        },
      },
      {
        title: "Create an API key",
        blocks: [
          {
            type: "ol",
            items: [
              "Under API keys, click New key.",
              "Give it a Name that says what uses it, for example Building management system.",
              "Under Access, tick what the key may read: Read screens, Read content, Read reports, Read emergencies. Tick the Act on screens options (control screens, publish content, write events, send emergency alerts) only if the system must change what's on screens.",
              "Optional: pick a Space to limit the key to that Space's screens and content (shown when you have more than one Space).",
              "Optional: under Allowed IP addresses, enter the address of the system that will use the key, one per line. Requests from anywhere else are refused.",
              "Optional: under Expires, choose when the key stops working.",
              "Click Create key, then Copy key and store it somewhere safe, and click Done.",
            ],
          },
          {
            type: "note",
            tone: "warning",
            text: "The full key is shown only once. If it is lost, create a new key and revoke the old one.",
          },
          {
            type: "p",
            text: "To change what a key can do, click Edit on its row. To stop a key working, click Revoke and confirm with Revoke key. Any system using it stops working immediately.",
          },
        ],
        screenshot: {
          src: "account/developer/02.png",
          alt: "The Copy your API key dialog, warning that the key is shown only once",
          todo: "After New key → Create key: the 'Copy your API key' dialog with Copy key / Done (key value blurred).",
        },
      },
      {
        title: "Add a webhook",
        blocks: [
          {
            type: "ol",
            items: [
              "Under Webhooks, click Add endpoint.",
              "Enter your Endpoint URL (it must start with https://).",
              "Under Events, keep Screen went offline and Screen came online ticked, or untick one.",
              "Click Add endpoint, then copy the signing secret that is shown once.",
            ],
          },
          {
            type: "p",
            text: "Your system can check that each message really comes from PulsePanels by verifying the webhook-signature header with that secret. It follows the Standard Webhooks format, so ready-made libraries can check it; click How to verify for an example. If your endpoint doesn't answer, PulsePanels tries again for up to 20 hours.",
          },
        ],
        screenshot: {
          src: "account/developer/03.png",
          alt: "The Add webhook endpoint dialog with Endpoint URL and the two events",
          todo: "Developer → Add endpoint: the 'Add webhook endpoint' dialog with https://example.com/hooks/pulsepanels and both events ticked.",
        },
      },
      {
        title: "Test, check, pause or remove a webhook",
        blocks: [
          {
            type: "ul",
            items: [
              "Test sends a test event and shows the answer from your endpoint.",
              "Deliveries lists the last 30 days of messages: whether each was delivered, the answer from your endpoint, and what was sent. Resend sends one again.",
              "The rotate icon creates a new signing secret. For 24 hours messages are signed with both the old and the new secret, so you can switch over without missing any.",
              "The On / Off box pauses a webhook without deleting it.",
              "The bin icon removes it.",
              "Each webhook shows the result of its last delivery, for example HTTP 200.",
            ],
          },
          {
            type: "note",
            tone: "info",
            text: "A webhook whose endpoint has failed every delivery for 24 hours is switched off automatically, with the reason shown. Fix the endpoint, then switch it back on.",
          },
        ],
      },
    ],
    related: [
      { label: "API overview & quick start", to: "/developers/api-overview" },
      { label: "Webhooks: events, signatures & retries", to: "/developers/webhooks" },
      { label: "Screen settings", to: "/account/screen-settings" },
      { label: "Roles & permissions", to: "/account/roles-and-permissions" },
    ],
    keywords: ["developer", "api", "api key", "rest", "webhook", "integration", "signature", "hmac", "offline", "bms", "building management", "openapi", "external id", "ip allowlist"],
  },

  {
    slug: "downloads",
    category: "account",
    route: "/account/downloads",
    title: "Download the player apps",
    summary:
      "Get the PulsePanels player for Android (including older Android 7 and 4.4 boxes), Samsung and LG TVs, and Windows from the Download Center.",
    intro:
      "The PulsePanels player is the app that turns a device into a screen. The Download Center on the Downloads tab has every version in one place, with install steps for each, so you can pick the right one for your hardware and then pair it.",
    prerequisites: [
      "You have a device to install the player on: an Android box or TV, a Samsung or LG signage display, or a Windows PC.",
    ],
    readingTime: 3,
    status: "draft",
    lastUpdated: "2026-10-01",
    steps: [
      {
        title: "Open the Download Center",
        blocks: [
          {
            type: "p",
            text: "Open your account (sidebar user menu → Account Settings or My account) and click Downloads — every role has this tab. Each card shows the latest version, its size and when it was updated, with a download button and install steps.",
          },
        ],
        screenshot: {
          src: "account/downloads/01.png",
          alt: "The Download Center with a card for each player",
          caption: "Every player download in one place.",
          todo: "Downloads tab (/account/downloads). Current image is from the local demo server, which has no player builds, so the cards read No build available yet / Coming soon — recapture on a server with builds so each card shows its version, size and download button.",
        },
      },
      {
        title: "Pick the player for your device",
        blocks: [
          {
            type: "table",
            head: ["Card", "For", "How to install"],
            rows: [
              ["PulsePanels Player — Android 8 and newer", "Android boxes, Android TVs and tablets", "Download APK on the device, or scan the QR code with an Android phone or tablet. Allow your browser to install unknown apps, then open the file."],
              ["PulsePanels Player (Android 7)", "Android 7 signage boxes only", "Download APK. The Android 8 build won't install on these boxes."],
              ["PulsePanels Player Lite (Android 4.4)", "Android 4.4 boxes", "Download APK and install it from a USB stick or over adb."],
              ["PulsePanels Player (Samsung TV)", "Samsung Tizen signage displays", "Download .wgt, copy it to a FAT32 USB stick and choose Install from USB on the display."],
              ["PulsePanels Player (LG TV)", "LG webOS signage displays", "Download .ipk, copy it to a FAT32 USB stick and install it from the display's app settings."],
              ["PulsePanels Player (Windows)", "Windows 10 and 11 PCs", "Download installer and run the setup file. It starts full-screen and opens at sign-in."],
            ],
          },
          {
            type: "p",
            text: "Each card has the exact steps for its device. iOS and Linux players are marked Coming soon. A card that says “No build available yet” or “Coming soon” has no download on your server yet.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Windows may warn about an unknown publisher — choose More info → Run anyway. To leave the Windows player later, press Ctrl+Shift+Q and enter your kiosk PIN (Account Settings → Screen settings).",
          },
        ],
        screenshot: {
          src: "account/downloads/02.png",
          alt: "The Samsung, LG and Windows player cards with their download buttons and install steps",
          todo: "Downloads tab scrolled to the Samsung TV, LG TV and Windows cards with their download buttons and install steps. Needs a server with the Tizen / webOS / Windows builds (the local demo has none, so it is not captured).",
        },
      },
      {
        title: "Install and pair",
        blocks: [
          {
            type: "p",
            text: "Open the player on your device. It shows a pairing code: in the dashboard, go to Screens, click Pair screen and enter the code.",
          },
          {
            type: "steps-ref",
            label: "See Pair a screen",
            to: "/screens/pair-a-screen",
          },
        ],
        screenshot: {
          src: "account/downloads/03.png",
          alt: "A freshly installed PulsePanels player showing Pair this screen with its 6-character code and a Scan to pair QR code",
          caption: "Install, open, then pair using the on-screen code or QR code.",
          todo: "The Android player's pairing screen at 1920x1080 (PulsePanels logo, Pair this screen + 6-character code, Scan to pair QR). Captured from the Android 7 player in an emulator pointed at the local demo.",
        },
      },
      {
        title: "On your own server (self-hosted)",
        blocks: [
          {
            type: "p",
            text: "If PulsePanels runs on your own server, the Download Center also shows This server's certificate with its fingerprint. When a screen first connects to your server, check that the fingerprint it shows matches this one before you accept it.",
          },
          {
            type: "p",
            text: "Under Remove the “Not secure” warning on your computers, click Download certificate and follow the steps for Windows, macOS, Firefox, or every computer in an office (Group Policy). After that, the browser trusts your server and the warning goes away.",
          },
        ],
      },
    ],
    related: [
      { label: "Pair a screen", to: "/screens/pair-a-screen" },
      { label: "Screen settings", to: "/account/screen-settings" },
    ],
    keywords: ["download", "player", "apk", "android 7", "android 4.4", "lite", "windows", "samsung", "tizen", "lg", "webos", "install", "certificate", "self-hosted", "on-premises"],
  },

  {
    slug: "audit-logs",
    category: "account",
    route: "/account/audit-logs",
    title: "Audit logs",
    summary:
      "See who changed what and when, search the trail, and export it as CSV.",
    intro:
      "The audit log records important actions in your account — who did what, to which item, in which Space, and when. Use it to answer “who changed this?”, to review activity, or for security and compliance.",
    prerequisites: [
      "You are the Owner, an Admin, or have the Auditor role.",
    ],
    readingTime: 2,
    status: "draft",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open Audit Logs",
        blocks: [
          {
            type: "p",
            text: "Open Account Settings and click Audit Logs. The newest actions come first. Each row shows the Actor, the Action, the Entity it was done to, the Workspace (Space), Details such as the IP address and browser, and the Time.",
          },
          {
            type: "p",
            text: "Changes to people and groups are recorded too — for example when someone is given a role through a group.",
          },
        ],
        screenshot: {
          src: "account/audit-logs/01.png",
          alt: "The Audit Logs tab: Actor, Action, Entity, Workspace, Details and Time for each entry",
          caption: "Who changed what, and when.",
          todo: "Audit Logs tab (/account/audit-logs) of the Acme Retail demo account — make sure the IP column shows no real address (use a local/dev session) and actor initials match the placeholder names.",
        },
      },
      {
        title: "Find an entry",
        blocks: [
          {
            type: "p",
            text: "Type in Search to look for an actor, action, item or Space, and pick a Range: Today, This Week, Last 7 Days, Last 30 Days or All Time. Reset clears both.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Search for a person's name with Range set to Last 30 Days to see everything they changed this month.",
          },
        ],
        screenshot: {
          src: "account/audit-logs/02.png",
          alt: "The audit log filtered by a search term and a range",
          caption: "Search and pick a range to narrow the trail.",
          todo: "Audit Logs with a search term typed and Range set to Last 7 Days (no real IPs visible).",
        },
      },
      {
        title: "See the details of a change",
        blocks: [
          {
            type: "p",
            text: "Click a row to open Audit Details: what happened, the item, the action, the Space and the time — and, for edits, the Changed fields with their value Before and After.",
          },
        ],
      },
      {
        title: "Export the log",
        blocks: [
          {
            type: "p",
            text: "Click Export CSV to download the entries that match your search and range. Refresh loads the newest entries.",
          },
        ],
      },
    ],
    related: [
      { label: "Users & invitations", to: "/account/users-and-invitations" },
      { label: "Roles & permissions", to: "/account/roles-and-permissions" },
      { label: "Groups", to: "/account/groups" },
    ],
    keywords: ["audit", "logs", "history", "activity", "who changed", "export", "csv", "compliance"],
  },
];
